<template>
    <div class="content-container">
        <div class="filter-container">
            <Filter
                :filter="filter"
                :statusList="statusList"
                class="filter"
                @addFilterStatus="addFilterStatus"
                @clearFilter="clearFilter"
                @removeFilterStatus="removeFilterStatus"
                @search="search"
                @setInterventionId="setInterventionId"
                @setPredictedDateFrom="setPredictedDateFrom"
                @setPredictedDateTo="setPredictedDateTo"
                @statusToggleSelectAll="statusToggleSelectAll"
            />
        </div>
        <div class="dataTable">
            <div style="margin-bottom: 8px">
                <h2>{{ count }} {{ $t('intervention.non-preventive.dataTable.title') }}</h2>
                <BorderedIconButton
                    v-if="!isExportDisabled"
                    :imgUrl="iconeXls"
                    :title="$t('list.export')"
                    @click="exportXls"
                />
            </div>
            <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
            <div v-else ref="content" class="datatable-container-smaller" @scroll="handleScroll">
                <DataTable
                    :count="+count"
                    :isLoading="isLoading"
                    :stop-propa-on-pdf="true"
                    :tableData="tableData"
                    :tableHeader="tableHeader"
                    @downloadPdf="downloadPdf"
                    @select="goToPage"
                    @sort="doSort"
                />
            </div>
        </div>
        <WaitingModal v-if="isExportXlsLoading" />
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import Filter from '@/modules/Common/components/Filter.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { SuiviInterventionActions } from '@/modules/SuiviInterventions/store/action-types';
import { FilterInterventions } from '@/modules/SuiviInterventions/services/SuiviInterventionService';
import { store } from '@/plugins/store';
import { SortType, sortValue } from '@/models/SortType';
import router from '@/router';
import DownloadPdf from '@/common/utils/DownloadPdf';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import iconeXls from '@/assets/img/xls.svg';
import i18n from '@/plugins/i18n';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import { Intervention } from '@/models/Intervention';

export default defineComponent({
    name: 'HistoriqueTravauxTab',
    components: {
        Filter,
        DataTable,
        LoaderComponent,
        BorderedIconButton,
        WaitingModal,
    },
    props: {
        phaseId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            filter: {
                statusList: [] as string[],
                interventionId: '',
                predictedDateFrom: '',
                predictedDateTo: '',
            },
        };
    },
    setup() {
        const content = ref<HTMLElement | null>(null);
        return {
            content,
            iconeXls,
        };
    },
    beforeMount() {
        this.filter.statusList = [...this.filterStatus];
        this.filter.interventionId = this.filterInterventionId;
        this.filter.predictedDateFrom = this.filterPredictedDateFrom;
        this.filter.predictedDateTo = this.filterPredictedDateTo;
    },
    computed: {
        historiqueState() {
            return store.state.equipementState.historiqueTravaux;
        },
        tableData() {
            return store.getters['equipementState/getHistoriqueTravaux'].map(
                ({
                    id,
                    objet,
                    type,
                    dateDebutPrevue,
                    dateDebutReelle,
                    dateFinReelle,
                    isAlarmante,
                    etat,
                    isRapportPresent,
                }: Intervention) => {
                    etat = statusName(etat);

                    return {
                        id: { pageName: 'interventionDetailView', value: id, label: id },
                        objet,
                        type,
                        dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                        dateDebutReelle: dateDebutReelle ? new Date(dateDebutReelle).toLocaleDateString() : '',
                        dateFinReelle: dateFinReelle ? new Date(dateFinReelle).toLocaleDateString() : '',
                        isAlarmante: isAlarmante ? i18n.global.t('yes') : i18n.global.t('no'),
                        statut: etat,
                        pdfExist: isRapportPresent,
                    };
                }
            );
        },
        tableHeader() {
            return store.getters['equipementState/getHistoriqueTravauxHeaders'];
        },
        count() {
            return store.getters['equipementState/getHistoriqueTravauxCount'];
        },
        isLoading() {
            return store.getters['equipementState/getHistoriqueTravauxLoading'];
        },
        page() {
            return store.getters['equipementState/getHistoriqueTravauxPage'];
        },
        numberOfPages() {
            return store.getters['equipementState/getHistoriqueTravauxNbOfPages'];
        },
        filterStatus(): string[] {
            return store.getters['equipementState/getHistoriqueTravauxFilter'].statusList;
        },
        filterInterventionId(): string {
            return store.getters['equipementState/getHistoriqueTravauxFilter'].interventionId;
        },
        filterPredictedDateFrom(): string {
            return store.getters['equipementState/getHistoriqueTravauxFilter'].predictedDateFrom;
        },
        filterPredictedDateTo(): string {
            return store.getters['equipementState/getHistoriqueTravauxFilter'].predictedDateTo;
        },
        statusList() {
            return Object.values(EtatsTravaux);
        },
        allStatusSelected() {
            return this.filter.statusList.length === this.statusList.length || this.filter.statusList.length === 0;
        },
        isExportDisabled() {
            return this.tableData.length === 0 || this.isLoading;
        },
        isExportXlsLoading() {
            return store.getters['equipementState/getHistoriqueTravauxExportXlsIsLoading'];
        },
    },
    mounted() {
        this.getInterventionData();
    },
    methods: {
        getInterventionData(page = 0) {
            const storeFilter = store.getters['equipementState/getHistoriqueTravauxFilter'];
            const filters: FilterInterventions = {
                page,
                id: storeFilter.interventionId !== '' ? storeFilter.interventionId : undefined,
                etats: this.allStatusSelected ? [] : storeFilter.statusList,
                phaseIds: [this.phaseId],
                dateDebutPeriodeRechercheParDate:
                    storeFilter.predictedDateFrom !== '' ? storeFilter.predictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    storeFilter.predictedDateTo !== '' ? storeFilter.predictedDateTo : undefined,
                sort: this.historiqueState.sort
                    ? sortValue(this.historiqueState.sort.sortData, this.historiqueState.sort.sortStatus)
                    : undefined,
                isBim: true,
            };

            store.dispatch(
                `suiviInterventionModuleState/${SuiviInterventionActions.GET_SUIVIS_INTERVENTION}`,
                filters,
                {
                    root: true,
                }
            );
        },
        search() {
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_FILTER_INTERVENTION_ID}`,
                this.filter.interventionId
            );
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_FILTER_STATUS}`, this.filter.statusList);
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_FILTER_PREDICTED_DATE_FROM}`,
                this.filter.predictedDateFrom
            );
            store.commit(
                `equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_FILTER_PREDICTED_DATE_TO}`,
                this.filter.predictedDateTo
            );

            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_ITEMS}`, []);
            this.getInterventionData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_SORT}`, sort);
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_ITEMS}`, []);
            this.getInterventionData();
        },
        setInterventionId(value: string) {
            this.filter.interventionId = value;
        },
        setPredictedDateFrom(value: string) {
            this.filter.predictedDateFrom = value;
        },
        setPredictedDateTo(value: string) {
            this.filter.predictedDateTo = value;
        },
        addFilterStatus(status: string) {
            this.filter.statusList.push(status);
        },
        removeFilterStatus(status: string) {
            this.filter.statusList = this.filter.statusList.filter(s => s !== status);
        },
        statusToggleSelectAll() {
            this.filter.statusList =
                this.filter.statusList.length === this.statusList.length ? [] : [...this.statusList];
        },
        clearFilter() {
            store.commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_RESET_FILTER}`);
            this.filter.interventionId = '';
            this.filter.predictedDateFrom = '';
            this.filter.predictedDateTo = '';
            this.filter.statusList = [...this.statusList];

            this.search();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.getInterventionData(this.page + 1);
            }
        },
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        downloadPdf(item: { id: { pageName: string; value: number } }) {
            DownloadPdf.getInterventionPdf(item.id.value, 'Rapport_intervention-');
        },
        exportXls() {
            const storeFilter = store.getters['equipementState/getHistoriqueTravauxFilter'];
            const filters: FilterInterventions = {
                page: 0,
                id: storeFilter.interventionId !== '' ? storeFilter.interventionId : undefined,
                etats: this.allStatusSelected ? [] : storeFilter.statusList,
                phaseIds: [this.phaseId],
                dateDebutPeriodeRechercheParDate:
                    storeFilter.predictedDateFrom !== '' ? storeFilter.predictedDateFrom : undefined,
                dateFinPeriodeRechercheParDate:
                    storeFilter.predictedDateTo !== '' ? storeFilter.predictedDateTo : undefined,
                sort: this.historiqueState.sort
                    ? sortValue(this.historiqueState.sort.sortData, this.historiqueState.sort.sortStatus)
                    : undefined,
                isBim: true,
            };

            store.dispatch(
                `suiviInterventionModuleState/${SuiviInterventionActions.EXPORT_XLS_INTERVENTIONS}`,
                filters,
                {
                    root: true,
                }
            );
        },
    },
});
</script>

<style scoped>
.dataTable > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
